import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { 
  doc, 
  collection, 
  addDoc, 
  updateDoc, 
  Timestamp 
} from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

// Styles object (unchanged from your original)
const styles = {
  container: {
    maxWidth: '48rem',
    margin: '2rem auto',
    backgroundColor: 'white',
    borderRadius: '0.75rem',
    boxShadow: '0 6px 12px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
  },
  header: {
    backgroundImage: 'linear-gradient(to right, #743ee4, #9252d6)',
    padding: '2rem',
    textAlign: 'center',
    color: 'white',
  },
  headerTitle: {
    fontSize: '2rem',
    fontWeight: '600',
    marginBottom: '0.5rem',
    fontFamily: 'Roboto, sans-serif',
  },
  headerSubtitle: {
    fontSize: '1.25rem',
    opacity: '0.9',
    fontFamily: 'Roboto, sans-serif',
    color: 'white',
  },
  content: {
    padding: '2rem',
    backgroundColor: '#f5f5f5',
  },
  songDetails: {
    backgroundColor: '#f5f3ff',
    borderRadius: '0.5rem',
    padding: '1.25rem',
    marginBottom: '1.5rem',
    borderLeft: '4px solid #c4b5fd',
  },
  songTitle: {
    fontWeight: '600',
    color: '#1f2937',
    fontSize: '1.25rem',
    marginBottom: '0.5rem',
    fontFamily: 'Roboto, sans-serif',
  },
  songTitleBold: {
    fontWeight: '700',
  },
  songInfo: {
    color: '#4b5563',
    marginBottom: '0.25rem',
    fontSize: '0.95rem',
    fontFamily: 'Roboto, sans-serif',
  },
  songOrderNumber: {
    color: '#4b5563',
    fontSize: '0.875rem',
    fontFamily: 'Roboto, sans-serif',
  },
  errorMessage: {
    backgroundColor: '#fef2f2',
    borderLeft: '4px solid #ef4444',
    color: '#b91c1c',
    padding: '1rem',
    marginBottom: '1.5rem',
    borderRadius: '0 0.5rem 0.5rem 0',
    fontFamily: 'Roboto, sans-serif',
  },
  discountOffer: {
    backgroundColor: '#fffbeb',
    borderLeft: '4px solid #fbbf24',
    padding: '1.25rem',
    marginBottom: '1.5rem',
    borderRadius: '0 0.5rem 0.5rem 0',
  },
  discountTitle: {
    display: 'block',
    color: '#1f2937',
    fontSize: '1.125rem',
    fontWeight: '600',
    marginBottom: '0.75rem',
    fontFamily: 'Roboto, sans-serif',
  },
  discountList: {
    color: '#4b5563',
    listStyle: 'none',
    paddingLeft: '0',
    fontSize: '0.95rem',
    fontFamily: 'Roboto, sans-serif',
  },
  discountItem: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '0.5rem',
  },
  discountBullet: {
    color: '#f59e0b',
    marginRight: '0.5rem',
    fontSize: '1.25rem',
  },
  formGroup: {
    marginBottom: '1.5rem',
  },
  label: {
    display: 'block',
    color: '#374151',
    fontWeight: '500',
    marginBottom: '0.5rem',
    fontFamily: 'Roboto, sans-serif',
  },
  starsContainer: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#f9fafb',
    padding: '0.75rem',
    borderRadius: '0.5rem',
    border: '1px solid #e5e7eb',
  },
  star: {
    cursor: 'pointer',
    marginRight: '0.25rem',
  },
  starRating: {
    marginLeft: '0.5rem',
    color: '#4b5563',
    fontSize: '0.95rem',
    fontFamily: 'Roboto, sans-serif',
  },
  textarea: {
    width: '100%',
    padding: '0.75rem 1rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.5rem',
    fontFamily: 'Roboto, sans-serif',
    fontSize: '0.95rem',
    resize: 'vertical',
  },
  videoUploadContainer: {
    backgroundColor: '#f5f3ff',
    borderLeft: '4px solid #c4b5fd',
    padding: '1.25rem',
    borderRadius: '0 0.5rem 0.5rem 0',
    marginBottom: '1rem',
  },
  dropZone: {
    border: '2px dashed #d1d5db',
    borderRadius: '0.5rem',
    padding: '1.5rem',
    textAlign: 'center',
    backgroundColor: 'white',
  },
  uploadedFile: {
    color: '#059669',
    fontWeight: '500',
    fontFamily: 'Roboto, sans-serif',
  },
  removeButton: {
    padding: '0.25rem 1rem',
    backgroundColor: '#fee2e2',
    color: '#b91c1c',
    borderRadius: '9999px',
    fontSize: '0.875rem',
    fontWeight: '500',
    marginTop: '0.5rem',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'Roboto, sans-serif',
    transition: 'background-color 0.2s ease',
  },
  uploadText: {
    color: '#6b7280',
    marginBottom: '0.75rem',
    fontSize: '0.95rem',
    fontFamily: 'Roboto, sans-serif',
  },
  fileInput: {
    display: 'none',
  },
  uploadButton: {
    display: 'inline-block',
    padding: '0.5rem 1.25rem',
    backgroundColor: '#ede9fe',
    color: '#6d28d9',
    borderRadius: '9999px',
    cursor: 'pointer',
    fontWeight: '500',
    fontFamily: 'Roboto, sans-serif',
    transition: 'background-color 0.2s ease',
  },
  fileSizeText: {
    fontSize: '0.875rem',
    color: '#6b7280',
    marginTop: '0.5rem',
    fontFamily: 'Roboto, sans-serif',
  },
  submitContainer: {
    textAlign: 'center',
    marginTop: '1.5rem',
  },
  submitButton: {
    padding: '0.75rem 2rem',
    borderRadius: '9999px',
    fontWeight: '600',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    backgroundColor: '#F58800',
    fontFamily: 'Roboto, sans-serif',
    transition: 'background-color 0.2s ease',
  },
  submitButtonHover: {
    backgroundColor: '#a855f7',
  },
  submitButtonDisabled: {
    backgroundColor: '#d4d4d8',
    cursor: 'not-allowed',
  },
  footer: {
    marginTop: '2rem',
    paddingTop: '1.5rem',
    borderTop: '1px solid #e5e7eb',
    textAlign: 'center',
    color: '#4b5563',
    fontSize: '0.875rem',
    fontFamily: 'Roboto, sans-serif',
  },
  copyright: {
    marginTop: '0.5rem',
    fontFamily: 'Roboto, sans-serif',
  },
  successContainer: {
    maxWidth: '48rem',
    margin: '2rem auto',
    backgroundColor: 'white',
    borderRadius: '0.75rem',
    boxShadow: '0 6px 12px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
  },
  successHeader: {
    backgroundImage: 'linear-gradient(to right, #9333ea, #a855f7)',
    padding: '2rem',
    textAlign: 'center',
    color: 'white',
  },
  successTitle: {
    fontSize: '1.75rem',
    fontWeight: '600',
    marginBottom: '0.5rem',
    fontFamily: 'Roboto, sans-serif',
  },
  successContent: {
    padding: '2rem',
    backgroundColor: '#f5f5f5',
  },
  successMessage: {
    backgroundColor: '#fffbeb',
    borderLeft: '4px solid #fbbf24',
    padding: '1rem',
    marginBottom: '1.5rem',
    borderRadius: '0 0.5rem 0.5rem 0',
  },
  successText: {
    fontWeight: '500',
    color: '#1f2937',
    fontFamily: 'Roboto, sans-serif',
  },
  successSubtext: {
    fontSize: '0.875rem',
    color: '#4b5563',
    marginTop: '0.5rem',
    fontFamily: 'Roboto, sans-serif',
  },
  dashboardButtonContainer: {
    textAlign: 'center',
    marginTop: '1.5rem',
  },
  dashboardButton: {
    display: 'inline-block',
    backgroundColor: '#9333ea',
    color: 'white',
    padding: '0.75rem 2rem',
    borderRadius: '9999px',
    fontWeight: '600',
    textDecoration: 'none',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'Roboto, sans-serif',
    transition: 'background-color 0.2s ease',
  },
  dashboardButtonHover: {
    backgroundColor: '#a855f7',
  },
};

// Star icon component (unchanged)
const StarIcon = ({ filled, onClick, onMouseEnter, onMouseLeave }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="36" 
    height="36" 
    viewBox="0 0 24 24" 
    fill={filled ? "#F58800" : "none"}
    stroke={filled ? "#F58800" : "#F58800"}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={styles.star}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
  </svg>
);

const ReviewForm = ({ initialSongDetails = null }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const songDetails = initialSongDetails || {};

  const [authVerified, setAuthVerified] = useState(false);

  useEffect(() => {
    console.log('ReviewForm received songDetails:', songDetails);
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        console.log('User authenticated:', { uid: user.uid, email: user.email });
        setAuthVerified(true);
      } else {
        console.log('No user authenticated');
        setAuthVerified(false);
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');
  const [discountCode, setDiscountCode] = useState('');

  if (!currentUser || !authVerified) {
    return <div style={{ ...styles.content, textAlign: 'center' }}>Please log in to submit a review.</div>;
  }

  const extractSongInfo = () => {
    console.log('Extracting song info from:', songDetails);
    console.log('Songs array:', songDetails.songs);
    console.log('Customer info:', songDetails.customerInfo);

    let songTitle = 'Your Song';
    if (songDetails.songs && songDetails.songs.length > 0) {
      const mainSong = songDetails.songs.find(s => s.isMainVersion) || songDetails.songs[0];
      songTitle = mainSong.name?.replace(/\.mp3$/, '') || 'Your Song';
      console.log('Found song title from songs array:', songTitle);
    } else if (songDetails.songFileName) {
      songTitle = songDetails.songFileName.replace(/^\d+_/, '').replace(/\.mp3$/, '');
      console.log('Found song title from songFileName:', songTitle);
    }

    let recipientName = 'You';
    if (songDetails.customerInfo && songDetails.customerInfo.fullName) {
      recipientName = songDetails.customerInfo.fullName;
      console.log('Found recipient name from customerInfo.fullName:', recipientName);
    } else if (songDetails.recipientName) {
      recipientName = songDetails.recipientName;
      console.log('Found recipient name from recipientName:', recipientName);
    } else if (songDetails.details && songDetails.details.recipientName) {
      recipientName = songDetails.details.recipientName;
      console.log('Found recipient name from details.recipientName:', recipientName);
    }

    let orderNumber = songDetails.orderNumber || 'N/A';
    if (orderNumber && !isNaN(orderNumber) && !String(orderNumber).includes('-')) {
      orderNumber = `MM-${orderNumber}`;
    }
    console.log('Formatted order number:', orderNumber);

    let genres = '';
    if (Array.isArray(songDetails.genres)) {
      genres = songDetails.genres.join(', ');
    }

    const packageType = songDetails.package || '';
    const occasion = songDetails.occasion || 
                     (songDetails.details && songDetails.details.occasion) || '';

    const result = {
      songTitle,
      recipientName,
      orderNumber,
      genres,
      packageType,
      occasion,
    };

    console.log('Final extracted song info:', result);
    return result;
  };

  const songInfo = extractSongInfo();

  const handleRatingHover = (hoveredValue) => setHoveredRating(hoveredValue);
  const handleRatingClick = (clickedValue) => setRating(clickedValue);
  const handleReviewTextChange = (e) => setReviewText(e.target.value);

  const handleVideoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      console.log('Selected file:', { name: file.name, size: file.size, type: file.type });
      if (!file.type.startsWith('video/')) {
        setError('Please upload a video file');
        return;
      }
      if (file.size > 300 * 1024 * 1024) {
        setError('Video file is too large (max 300MB)');
        return;
      }
      setVideoFile(file);
      setError('');
    }
  };

  const generateDiscountCode = (hasVideo) => {
    const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let result = hasVideo ? 'VIDEO50-' : 'REVIEW10-';
    for (let i = 0; i < 6; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
  };

  const uploadVideo = async (file) => {
    if (!file || !songDetails.id) {
      console.log('No file or song ID provided for upload');
      return null;
    }

    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated for upload');
    }

    const storage = getStorage();
    const videoFileName = `reviews/${songDetails.id}/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, videoFileName);

    console.log('Starting video upload:', {
      path: videoFileName,
      userId: user.uid,
      file: { name: file.name, size: file.size, type: file.type },
    });

    try {
      const uploadTask = uploadBytesResumable(storageRef, file);
      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            console.log('Upload progress:', `${progress}%`);
          },
          (error) => {
            console.error('Upload error:', { code: error.code, message: error.message });
            reject(error);
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              console.log('Upload completed, download URL:', downloadURL);
              resolve(downloadURL);
            } catch (error) {
              console.error('Error getting download URL:', error);
              reject(error);
            }
          }
        );
      });
    } catch (error) {
      console.error('Upload exception:', error);
      throw error;
    }
  };

  const sanitizeData = (data) => {
    const sanitized = {};
    for (const [key, value] of Object.entries(data)) {
      if (value === undefined || value === null) {
        sanitized[key] = '';
      } else if (typeof value === 'object' && value instanceof Timestamp) {
        sanitized[key] = value;
      } else {
        sanitized[key] = value;
      }
    }
    return sanitized;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (rating === 0) {
      setError('Please select a star rating');
      return;
    }
    if (reviewText.trim() === '') {
      setError('Please write a review');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const hasVideo = !!videoFile;
      const newDiscountCode = generateDiscountCode(hasVideo);
      setDiscountCode(newDiscountCode);

      let videoUrl = null;
      if (videoFile) {
        console.log('Uploading video file:', videoFile.name);
        videoUrl = await uploadVideo(videoFile);
      }

      const reviewData = {
        rating,
        reviewText,
        hasVideo,
        videoUrl,
        songId: songDetails.id,
        discountCode: newDiscountCode,
        discountAmount: hasVideo ? 50 : 10,
        orderNumber: songInfo.orderNumber,
        customerName: songInfo.recipientName,
        customerEmail: songDetails?.customerInfo?.email || '',
        createdAt: Timestamp.now(),
        songTitle: songInfo.songTitle,
        userId: currentUser.uid,
      };

      const sanitizedReviewData = sanitizeData(reviewData);
      console.log('Sanitized review data:', sanitizedReviewData);

      const reviewsRef = collection(db, 'reviews');
      const reviewDocRef = await addDoc(reviewsRef, sanitizedReviewData);
      console.log('Review saved with ID:', reviewDocRef.id);

      if (songDetails.id) {
        try {
          const songRef = doc(db, 'songs', songDetails.id);
          await updateDoc(songRef, {
            hasReview: true,
            reviewedAt: Timestamp.now(),
          });
          console.log('Song document updated with ID:', songDetails.id);
        } catch (updateErr) {
          console.warn('Failed to update song document:', updateErr.message);
        }
      }

      setSubmitSuccess(true);
    } catch (err) {
      console.error('Submit error:', { message: err.message, code: err.code });
      setError(`Error submitting your review: ${err.message}. Please try again or contact support.`);
      setSubmitSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div style={styles.successContainer}>
        <div style={styles.successHeader}>
          <h2 style={styles.successTitle}>Thank You For Your Review!</h2>
        </div>
        <div style={styles.successContent}>
          <div style={styles.successMessage}>
            <p style={styles.successText}>
              {videoFile 
                ? "You've earned 50% off your next order with your video review!" 
                : "You've earned 10% off your next order!"}
            </p>
            <p style={styles.successSubtext}>
              Your discount code <strong>{discountCode}</strong> will be emailed to you shortly.
            </p>
          </div>
          <div style={styles.dashboardButtonContainer}>
            <button 
              onClick={() => navigate('/dashboard')}
              style={styles.dashboardButton}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = styles.dashboardButtonHover.backgroundColor}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = styles.dashboardButton.backgroundColor}
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.headerTitle}>Love Your Custom Song?</h1>
        <p style={styles.headerSubtitle}>We'd appreciate if you could take a moment to share your feedback!</p>
      </div>
      
      <div style={styles.content}>
        <div style={styles.songDetails}>
          <h2 style={styles.songTitle}>
            Your song: <span style={styles.songTitleBold}>{songInfo.songTitle}</span>
          </h2>
          <p style={styles.songInfo}>
            Created for: {songInfo.recipientName}
          </p>
          {songInfo.packageType && (
            <p style={styles.songInfo}>
              Package: {songInfo.packageType}
            </p>
          )}
          {songInfo.occasion && (
            <p style={styles.songInfo}>
              Occasion: {songInfo.occasion}
            </p>
          )}
          {songInfo.genres && (
            <p style={styles.songInfo}>
              Genres: {songInfo.genres}
            </p>
          )}
          <p style={styles.songOrderNumber}>
            Order: {songInfo.orderNumber}
          </p>
        </div>
        
        {error && (
          <div style={styles.errorMessage}>
            {error}
          </div>
        )}
        
        <div style={styles.discountOffer}>
          <strong style={styles.discountTitle}>Special Discount Offer</strong>
          <ul style={styles.discountList}>
            <li style={styles.discountItem}>
              <span style={styles.discountBullet}>•</span>
              <span>Leave a star rating and written review to get <strong>10% OFF</strong> your next order</span>
            </li>
            <li style={styles.discountItem}>
              <span style={styles.discountBullet}>•</span>
              <span>OR upload a video of the recipient's reaction to get <strong>50% OFF</strong> your next order</span>
            </li>
          </ul>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label}>How would you rate your custom song?</label>
            <div style={styles.starsContainer}>
              {[1, 2, 3, 4, 5].map((star) => (
                <StarIcon
                  key={star}
                  filled={(hoveredRating || rating) >= star}
                  onClick={() => handleRatingClick(star)}
                  onMouseEnter={() => handleRatingHover(star)}
                  onMouseLeave={() => handleRatingHover(0)}
                />
              ))}
              <span style={styles.starRating}>
                {rating > 0 ? `${rating} star${rating !== 1 ? 's' : ''}` : 'Select a rating'}
              </span>
            </div>
          </div>
          
          <div style={styles.formGroup}>
            <label htmlFor="reviewText" style={styles.label}>
              Tell us about your experience:
            </label>
            <textarea
              id="reviewText"
              rows="4"
              style={styles.textarea}
              value={reviewText}
              onChange={handleReviewTextChange}
              placeholder="Write your review here..."
            ></textarea>
          </div>
          
          <div style={styles.formGroup}>
            <div style={styles.videoUploadContainer}>
              <label style={styles.label}>
                Upload a reaction video (for 50% discount):
              </label>
              <div style={styles.dropZone}>
                {videoFile ? (
                  <div style={styles.uploadedFile}>
                    <div>File uploaded: {videoFile.name}</div>
                    <button 
                      type="button"
                      style={styles.removeButton}
                      onClick={() => setVideoFile(null)}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fecaca'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fee2e2'}
                    >
                      Remove Video
                    </button>
                  </div>
                ) : (
                  <>
                    <p style={styles.uploadText}>Upload a video of the recipient's reaction when they first heard their custom song!</p>
                    <input
                      type="file"
                      accept="video/*"
                      style={styles.fileInput}
                      id="video-upload"
                      onChange={handleVideoUpload}
                    />
                    <label
                      htmlFor="video-upload"
                      style={styles.uploadButton}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#ddd6fe'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ede9fe'}
                    >
                      Select Video File
                    </label>
                  </>
                )}
              </div>
              <p style={styles.fileSizeText}>
                Max file size: 300MB
              </p>
            </div>
          </div>
          
          <div style={styles.submitContainer}>
            <button
              type="submit"
              disabled={isSubmitting}
              style={isSubmitting ? { ...styles.submitButton, ...styles.submitButtonDisabled } : styles.submitButton}
              onMouseEnter={(e) => !isSubmitting && (e.currentTarget.style.backgroundColor = styles.submitButtonHover.backgroundColor)}
              onMouseLeave={(e) => !isSubmitting && (e.currentTarget.style.backgroundColor = styles.submitButton.backgroundColor)}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Review'}
            </button>
          </div>
        </form>
        
        <div style={styles.footer}>
          <p>Thank you for choosing MakeMeSongs to create your special musical memory!</p>
          <p style={styles.copyright}>© 2025 MakeMeSongs. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default ReviewForm;